<template>
    <div v-if="isVisible" class="launcher">
        <div aria-live="polite" aria-atomic="true">
            <BaseNotification v-if="status" :theme="status.theme" :text="status.text" class="status" @dismiss="status = null" />
        </div>

        <BasePopover v-model:open="open" as-child modal side="top" align="end" :side-offset="8" :collision-padding="16" content-class="w-88 max-w-[calc(100vw-2rem)] p-4">
            <template #trigger>
                <BaseButton theme="neutral" class="trigger" :title="title" :aria-label="`Open ${title.toLowerCase()}`">
                    <i class="bi bi-magic" aria-hidden="true"></i>
                </BaseButton>
            </template>
            <template #content>
                <slot />
            </template>
        </BasePopover>
    </div>
</template>

<script lang="ts">
import type { BaseNotificationTheme } from "~vue/components/BaseNotification.vue";

export interface DemoToolsStatus {
    theme: BaseNotificationTheme;
    text: string;
}
</script>

<script setup lang="ts">
import type { UserDetails } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePopover from "~vue/components/BasePopover.vue";
import { computed, inject, type Ref, ref } from "vue";

const { title } = defineProps<{
    // Popover heading owned by the host; the trigger's title and aria-label derive from it.
    title: string;
}>();
const open = defineModel<boolean>("open", { default: false });

const userDetails = inject<Ref<UserDetails>>("user_details");
const status = ref<DemoToolsStatus | null>(null);

const isVisible = computed(() => userDetails?.value.is_staff === true);

function finish(result: DemoToolsStatus): void {
    status.value = result;
    open.value = false;
}

defineExpose({ finish });
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    /* --mobile-nav-total-height is set on :root at every width, so the md
       override has to be an explicit media query that comes after it. */
    .launcher {
        @apply fixed right-4 z-20 flex max-w-[calc(100vw-2rem)] flex-col items-end gap-3 md:right-6;
        bottom: calc(var(--mobile-nav-total-height, 0px) + 1rem);

        @media (width >= theme(--breakpoint-md)) {
            bottom: 1.5rem;
        }
    }

    .status {
        @apply max-w-sm shadow-lg;
    }

    .trigger {
        @apply inline-flex size-10 items-center justify-center rounded-full p-0 opacity-70 shadow-lg transition-opacity hover:opacity-100 focus-visible:opacity-100;
    }
}
</style>
